import { z } from 'zod'

import type { CustomComponent } from '@flows/core/types/CustomComponent'
import type { AnyStepConfiguration } from '@flows/core/types/stepTypes'
import { SubFlow } from '@flows/insurance/types/subFlowTypes'

export type PaymentConfirmation = CustomComponent<string, [], string[], Record<string, string>>

const NEW_DESIGN_HEADER = [
  {
    type: 'harmonyTitle',
    fontSize: 'primary',
    text: {
      type: 'translatableText',
      translationKey: 'sharedFlows_common:steps.payment.heading',
    },
  },
] as const

const NEW_DESIGN_WAIVER_NOTES = [
  {
    type: 'infoBox',
    showInfoIcon: true,
    bgColor: 'grey100',
    text: {
      type: 'translatableText',
      translationKey: 'sharedFlows_common:steps.payment.personalConsultationWaiver.info',
    },
  },
  {
    type: 'customComponent',
    name: 'ContractDocuments',
    inputs: [],
    outputs: [],
    props: {},
  },
] as const

export const buildPaymentStep = (
  i18nNamespace: string,
  options?: {
    extraConfirmations?: readonly PaymentConfirmation[]
    /** Heading, contract documents and a single immediate coverage consent. */
    newDesign?: boolean
  }
): AnyStepConfiguration => {
  const newDesign = options?.newDesign === true
  return {
    schema: z
      .object({
        personalConsultationWaive: z.literal(true),
        ...Object.fromEntries(
          (options?.extraConfirmations ?? []).flatMap((confirmation) =>
            confirmation.outputs.map((valueName) => [valueName, z.literal(true)])
          )
        ),
      })
      .passthrough(),
    stepContent: [
      ...(newDesign ? NEW_DESIGN_HEADER : []),
      {
        type: 'title',
        ...(newDesign ? { isQuestion: true, spaceBefore: 'large' } : {}),
        text: {
          type: 'translatableText',
          translationKey: `${i18nNamespace}:steps.payment.title`,
        },
      },
      {
        type: 'customComponent',
        name: 'StepPayment',
        inputs: [],
        outputs: [],
        props: {},
      },
      {
        type: 'checkbox',
        blockTitle: {
          type: 'translatableText',
          translationKey: 'sharedFlows_common:steps.payment.personalConsultationWaiver.title',
        },
        fields: [
          {
            name: 'personalConsultationWaive',
            title: {
              type: 'translatableText',
              translationKey: newDesign
                ? 'sharedFlows_common:steps.payment.personalConsultationWaiver.newDesignText'
                : 'sharedFlows_common:steps.payment.personalConsultationWaiver.text',
            },
          },
        ],
        bgColor: 'grey100',
      },
      ...(newDesign ? NEW_DESIGN_WAIVER_NOTES : []),
      ...(options?.extraConfirmations ?? []),
      {
        type: 'customComponent',
        name: 'ImmediateCoverageConsent',
        inputs: [],
        outputs: newDesign
          ? ['immediateCoverageStartConsent', 'immediateCoverageConsentMissing']
          : [
              'immediateCoverageStartConsent',
              'immediateCoverageWithdrawalAcknowledgement',
              'immediateCoverageConsentMissing',
            ],
        props: {},
      },
      {
        type: 'infoBox',
        showInfoIcon: true,
        iconName: 'HeartStraight',
        iconWeight: 'fill',
        iconColor: 'success',
        bgColor: 'grey100',
        color: 'grey700',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_common:steps.payment.seekAdvice.text',
        },
      },
      {
        type: 'infoBox',
        bgColor: 'white',
        showInfoIcon: true,
        iconName: 'Lock',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_common:steps.payment.encryptionNote',
        },
      },
    ],
    showOrderSummary: true,
    subFlow: SubFlow.PaymentAndConfirmation,
    nextStep: 'success',
  }
}
